import type { GetVendorQuery, Language } from '@/generated/graphql'
import { populateFieldsWithTranslations } from '@caterdesk/utils--translations'
import { useMemo } from 'react'
import { type MenuVendor, validateMenuVendor } from '../domain/menu-vendor'
import { useAppError } from '@/components/error-page/errors'

type Props = {
  language: Language
  vendorDetails?: GetVendorQuery['vendor'] | null
  isLoading?: boolean
}

type UseMenuPageVendorResult =
  | { status: 'loading'; vendor: null }
  | { status: 'success'; vendor: MenuVendor }

export const useMenuPageVendor = ({
  vendorDetails,
  language,
  isLoading = false,
}: Props): UseMenuPageVendorResult => {
  const { throwAppError } = useAppError()

  const vendorTranslated = useMemo(() => {
    if (vendorDetails === undefined) return undefined
    if (!vendorDetails) return null
    return populateFieldsWithTranslations(vendorDetails, language)
  }, [vendorDetails, language])

  const vendorValidation = useMemo(() => {
    if (vendorTranslated === undefined) return { type: 'loading' } as const
    if (vendorTranslated === null) return { type: 'missing' } as const
    return validateMenuVendor(vendorTranslated)
  }, [vendorTranslated])

  if (vendorValidation.type === 'loading') {
    return { status: 'loading', vendor: null }
  }

  if (vendorValidation.type === 'missing') {
    if (isLoading) {
      return { status: 'loading', vendor: null }
    }

    return throwAppError({
      type: 'COULD_NOT_FETCH_VENDOR',
      permalink: vendorDetails?.permalink ?? '',
      cause: 'Could not load vendor details',
    })
  }

  if (vendorValidation.type === 'error') {
    return throwAppError(vendorValidation.error)
  }

  return {
    status: 'success',
    vendor: vendorValidation.vendor,
  }
}
